import React from "react";
import { Circle, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";

// Everything drawn on a Flockout map, shared by the homepage hero and the big map.

export const BASEMAPS = {
  dark: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    ref: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    ref: "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
    attribution: "Imagery &copy; Esri, Maxar, Earthstar Geographics",
  },
};
export const COLORS = { drone: "#ff2e88", dji: "#ff2e88", other: "#c77dff", sky: "#ff5b3a", alpr: "#ffd400", cam: "#9a96ad", node: "#3ddc97" };

export function ago(iso) {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return `${Math.round(s)}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 129600) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

const icons = {};
function icon(kind, glyph) {
  const key = kind + glyph;
  if (!icons[key]) {
    const color = COLORS[kind] || COLORS.drone;
    icons[key] = L.divIcon({
      className: "fo-pin",
      html: `<span style="display:flex;align-items:center;justify-content:center;width:22px;height:22px;
        background:${color};color:#0b0b12;font:700 12px Lato,sans-serif;border:2px solid #0b0b12;
        box-shadow:0 0 0 1px ${color}">${glyph}</span>`,
      iconSize: [22, 22],
      iconAnchor: [11, 11],
      popupAnchor: [0, -12],
    });
  }
  return icons[key];
}

// Sky alerts only know which node saw them, so spread them around it instead of stacking.
function jitter(lat, lon, id) {
  const a = (id * 137.508 * Math.PI) / 180;
  const r = 0.0012 * (1 + (id % 4));
  return [lat + r * Math.sin(a), lon + r * Math.cos(a)];
}

/** Drones, flight paths, sky alerts, plate readers and boxes, from a skywatch/map/ response. */
function SkyLayers({ data, layers }) {
  const pinnedCams = data.cameras.filter(
    (c) => c.latitude != null && ((c.category === "alpr" && layers.alpr) || (c.mine && c.category !== "alpr" && layers.mycams))
  );
  return (
    <>
      {layers.nodes &&
        data.nodes.map((n) =>
          n.mine ? (
            <Marker key={`n${n.id}`} position={[n.lat, n.lon]} icon={icon("node", "⌂")}>
              <Popup>
                <b>{n.name}</b>
                <div>Your box · last check-in {ago(n.last_seen_at)}</div>
              </Popup>
            </Marker>
          ) : (
            <Circle
              key={`n${n.id}`}
              center={[n.lat, n.lon]}
              radius={300}
              pathOptions={{ color: COLORS.node, weight: 1, dashArray: "4 4", fillOpacity: 0.06 }}
            >
              <Popup>A member's box (approximate spot) · last check-in {ago(n.last_seen_at)}</Popup>
            </Circle>
          )
        )}

      {layers.drones &&
        data.drones.map((d) => {
          const pts = d.points.map((p) => [p[0], p[1]]);
          const last = d.points[d.points.length - 1];
          const color = d.maker === "DJI" ? COLORS.dji : COLORS.other;
          return (
            <React.Fragment key={`d${d.serial}`}>
              {pts.length > 1 && <Polyline positions={pts} pathOptions={{ color, weight: 2, opacity: 0.8 }} />}
              {last && (
                <Marker position={[last[0], last[1]]} icon={icon(d.maker === "DJI" ? "dji" : "other", "✕")}>
                  <Popup>
                    <b>{d.serial}</b>
                    <div>{[d.maker || "unknown maker", d.ua_type].filter(Boolean).join(" · ")}</div>
                    <div>
                      last seen {ago(d.last_seen_at)}
                      {last[2] != null && ` · ${Math.round(last[2])} m up`}
                    </div>
                    {d.events.some((e) => e.event === "repeat") && (
                      <div><span className="fo-pill crit">seen on several days</span></div>
                    )}
                    {d.events.some((e) => e.event === "loiter") && <div>hung around near a box</div>}
                    {d.pilot && <div>pilot (only you see this): {d.pilot.lat.toFixed(5)}, {d.pilot.lon.toFixed(5)}</div>}
                  </Popup>
                </Marker>
              )}
              {d.pilot && last && (
                <Polyline
                  positions={[[d.pilot.lat, d.pilot.lon], [last[0], last[1]]]}
                  pathOptions={{ color: COLORS.alpr, weight: 1, dashArray: "3 5" }}
                />
              )}
            </React.Fragment>
          );
        })}

      {layers.sky &&
        data.sky
          .filter((s) => s.lat != null)
          .map((s) => (
            <Marker key={`s${s.id}`} position={jitter(s.lat, s.lon, s.id)} icon={icon("sky", s.event === "escalate" ? "!!" : "?")}>
              <Popup>
                <b>{s.event === "escalate" ? "Unidentified, escalated" : "Unidentified object"}</b>
                <div>{s.label} · {ago(s.at)}</div>
                <div>{s.reason}</div>
                {s.approximate && <div className="fo-meta">seen by someone else's box, position approximate</div>}
              </Popup>
            </Marker>
          ))}

      {pinnedCams.map((c) => (
        <Marker key={`c${c.id}`} position={[c.latitude, c.longitude]} icon={icon(c.category === "alpr" ? "alpr" : "cam", c.category === "alpr" ? "▣" : "◉")}>
          <Popup>
            <b>{c.category === "alpr" ? "License plate reader" : "Camera"}</b>
            <div>{c.vendor || "unknown vendor"} · heard {ago(c.last_seen_at)}</div>
            {c.notes && <div>{c.notes}</div>}
          </Popup>
        </Marker>
      ))}
    </>
  );
}

export default SkyLayers;
